import type { Route } from './+types/infrastructure';
import type { ResourceItem } from '@/components/sections/resource-list';
import { CtaBanner } from '@/components/sections/cta-banner';
import { HeroSection } from '@/components/sections/hero-section';
import { ImageBanner } from '@/components/sections/image-banner';
import { ResourceListSection } from '@/components/sections/resource-list';
import { inheritRootLdJson } from '@/utils/schema';

const AERIAL_VIEW = '/static/infrastructure/aerial-view.png';

const TRANSPORT_NETWORKS: Array<ResourceItem> = [
  {
    id: 'bunya-highway',
    title: 'Bunya Highway (State Route 49)',
    href: 'https://id.com.au',
    hrefLabel: 'id.com.au',
    description:
      'Acts as the critical road connector linking Kingaroy north to Goomeri and southwest toward Dalby, carrying an estimated 900–2,600 vehicles per day depending on the town boundary and agricultural segment.',
  },
  {
    id: 'burnett-highway',
    title: 'Burnett Highway (A3)',
    href: 'https://id.com.au',
    hrefLabel: 'id.com.au',
    description: 'Serves as a major regional north-south freight spine passing right through Nanango.',
  },
  {
    id: 'daguilar-highway',
    title: "D'Aguilar Highway (A3)",
    href: 'https://id.com.au',
    hrefLabel: 'id.com.au',
    description: 'Functions as the primary highway corridor linking Kingaroy and Yarraman directly to Brisbane.',
  },
  {
    id: 'local-road-maintenance-network',
    title: 'Local Road Maintenance Network',
    href: 'https://www.southburnett.qld.gov.au',
    hrefLabel: 'southburnett.qld.gov.au',
    description: 'Comprises over 3,200 km of council-managed roads, pathways, and drainage pathways.',
  },
];

const INDUSTRY_ENERGY_INFRASTRUCTURE: Array<ResourceItem> = [
  {
    id: 'tarong-power-stations',
    title: 'Tarong Power Stations',
    href: 'https://stanwell.com',
    hrefLabel: 'stanwell.com',
    description: "A massive energy generation asset located near Kingaroy that provides roughly 20% of Queensland's electricity requirements.",
  },
  {
    id: 'tarong-battery-energy-storage-system',
    title: 'Tarong Battery Energy Storage System',
    href: 'https://stanwell.com',
    hrefLabel: 'stanwell.com',
    description: 'A newly integrated 300 MW big battery system designed to provide essential grid firming capacity.',
  },
  {
    id: 'swickers-kingaroy-pork-processing',
    title: 'Swickers Kingaroy Pork Processing',
    href: 'https://remplan.com.au',
    hrefLabel: 'remplan.com.au',
    description: 'One of the single largest manufacturing facilities and primary employers in the South Burnett.',
  },
];

const HEALTH_EDUCATION_INFRASTRUCTURE: Array<ResourceItem> = [
  {
    id: 'kingaroy-public-hospital',
    title: 'Kingaroy Public Hospital',
    href: 'https://health.qld.gov.au',
    hrefLabel: 'health.qld.gov.au',
    description: 'Completed a comprehensive $92.5 million multi-level clinical services building expansion.',
  },
  {
    id: 'tafe-queensland-kingaroy-campus',
    title: 'TAFE Queensland Kingaroy Campus',
    href: 'https://tafeqld.edu.au',
    hrefLabel: 'tafeqld.edu.au',
    description: 'Operates trade, agricultural, and technical vocational learning spaces right off the Bunya Highway.',
  },
  {
    id: 'regional-state-school-network',
    title: 'Regional State School Network',
    href: 'https://qed.qld.gov.au',
    hrefLabel: 'qed.qld.gov.au',
    description: 'Core public education infrastructure mapping out secondary campuses across Kingaroy, Nanango, and Murgon.',
  },
];

const UPCOMING_PROJECTS: Array<ResourceItem> = [
  {
    id: 'kingaroy-airport-workforce-accommodation',
    title: 'Kingaroy Airport Workforce Accommodation',
    href: 'https://southburnett.com.au',
    hrefLabel: 'southburnett.com.au',
    description:
      'A development application has been lodged for a 434-person temporary worker camp at Warren Truss Drive to house construction crews for the Tarong West Wind Farm.',
  },
  {
    id: 'tarong-west-wind-farm-development',
    title: 'Tarong West Wind Farm Development',
    href: 'https://areasearch.com.au',
    hrefLabel: 'areasearch.com.au',
    description:
      "Located 30km west of Kingaroy, this 436.5MW installation will feature 97 wind turbines and is set to be Australia's largest publicly owned wind farm.",
  },
  {
    id: 'draft-wide-bay-burnett-regional-plan',
    title: 'Draft Wide Bay Burnett Regional Plan and Infrastructure Plan',
    href: 'https://planning.qld.gov.au',
    hrefLabel: 'planning.qld.gov.au',
    description: 'This statutory 20-year blueprint aligns land use with state infrastructure spending targets through to 2046.',
  },
  {
    id: 'regional-infrastructure-capital-works',
    title: 'Regional Infrastructure Capital Works',
    href: 'https://www.southburnett.qld.gov.au',
    hrefLabel: 'southburnett.qld.gov.au',
    description:
      'The local council has committed millions to infrastructure asset preservation, prioritising local utilities and road transport lines.',
  },
];

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'Infrastructure & Investment | Kingaroy & South Burnett | Meet Real Estate' },
    {
      name: 'description',
      content:
        "The infrastructure shaping Kingaroy and the South Burnett — established transport, energy, health and education assets, plus what's coming next, from the Tarong West Wind Farm to the regional planning scheme guiding growth through to 2046.",
    },
  ];
}

export default function Infrastructure() {
  return (
    <main>
      <HeroSection
        eyebrow="Infrastructure & Investment"
        heading="Infrastructure & Investment"
        description="The transport, energy, health and education assets already serving Kingaroy and the South Burnett — plus the major developments on the way. Worth knowing about whether you're buying, selling, or simply keeping an eye on the region's growth."
        primaryCta={{ label: 'Get in Touch', to: '#contact' }}
      />

      <ImageBanner
        id="aerial-overview"
        eyebrow="Regional Overview"
        heading="Kingaroy, from Above"
        description="The Bunya Highway interchange feeding straight into the town grid — the corridor most of the region's existing and upcoming infrastructure sits along."
        image={AERIAL_VIEW}
        imageAlt="Aerial view of Kingaroy and the Bunya Highway interchange"
        dim
      />

      <ResourceListSection
        id="transport"
        className="scroll-mt-20"
        eyebrow="Existing Infrastructure"
        heading="Transport Networks"
        resources={TRANSPORT_NETWORKS}
      />

      <ResourceListSection
        id="industry-energy"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Existing Infrastructure"
        heading="Industry & Energy"
        resources={INDUSTRY_ENERGY_INFRASTRUCTURE}
      />

      <ResourceListSection
        id="health-education"
        className="scroll-mt-20"
        eyebrow="Existing Infrastructure"
        heading="Health & Education"
        resources={HEALTH_EDUCATION_INFRASTRUCTURE}
      />

      <ResourceListSection
        id="projects"
        className="section--navy-mid scroll-mt-20"
        eyebrow="What's Coming"
        heading="Upcoming Infrastructure Projects"
        resources={UPCOMING_PROJECTS}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        heading="Wondering How These Projects Could Affect Your Property?"
        description="Whether you're buying, selling, or just curious about the region's outlook, a confidential conversation costs you nothing."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
